"use client"

import * as React from "react"
import { useUser } from "@clerk/clerk-react"
import type { FinanceHomeOverview, MembersHomeOverview } from "@ibada/shared-types/overview"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useFormBranchContext } from "@/features/auth/hooks/use-form-branch-context"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { BudgetScope } from "@/features/budgeting/model/budget-enums"
import type { BudgetOverviewPayload } from "@/features/budgeting/model/budget-types"
import { useActiveBudgetOverviewQuery } from "@/features/budgeting/services/budgets-query"
import { useFinanceHomeQuery, useMembersHomeQuery } from "@/features/dashboard/home-d4/hooks/use-home-dashboard-queries"
import { HomeTileId, HomeTileState, tileStateFor } from "@/features/dashboard/home-d4/lib/home-grid-layout"
import type { UseDashboardDateRangeResult } from "@/features/dashboard/hooks/use-dashboard-date-range"
import type { WorkQueueSummary } from "@/features/work-queue/model/work-queue.types"
import { useWorkQueueBranchSummaryQuery } from "@/features/work-queue/services/work-queue-query"
import { useLocale } from "@/i18n"
import { useFeatureAccess } from "@/shared/feature-flags"
import { useChurchNow } from "@/shared/hooks/use-church-now"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

export type HomeD4Data = {
  /** Per tile: loading, ready or absent (no permission, flag off, failed read). Drives registry visibility. */
  states: Record<HomeTileId, HomeTileState>
  finance: FinanceHomeOverview | undefined
  members: MembersHomeOverview | undefined
  budget: BudgetOverviewPayload | undefined
  approvals: WorkQueueSummary | undefined
  can: { editMembers: boolean; recordIncome: boolean; createBudget: boolean }
  /** A branch-relative admin path as a full URL. */
  href: (path: string) => string
  firstName: string | null
  dateLabel: string
}

const ABSENT_STATES: Record<HomeTileId, HomeTileState> = Object.fromEntries(
  Object.values(HomeTileId).map((id) => [id, HomeTileState.Absent]),
) as Record<HomeTileId, HomeTileState>

/**
 * Everything the D4 tiles read, fetched once for the whole board. `enabled` is false everywhere but the branch
 * home (the parish board and the Branch Details console never fetch it), and every tile is then absent.
 */
export function useHomeD4Data(period: UseDashboardDateRangeResult, options: { enabled: boolean }): HomeD4Data {
  const { enabled } = options
  const locale = useLocale()
  const churchNow = useChurchNow()
  const { user } = useUser()

  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const { activeBranchId } = useFormBranchContext()
  const branchSlug = useActiveBranchSlug() ?? ""
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const href = React.useCallback((path: string) => buildBranchAdminUrl(branchSlug, path, workspaceSlug), [branchSlug, workspaceSlug])

  const members = useModulePermission(WorkspaceAccessModule.MEMBERS)
  const finance = useModulePermission(WorkspaceAccessModule.FINANCE)
  const budgeting = useModulePermission(WorkspaceAccessModule.BUDGETING)
  const budgetingFeature = useFeatureAccess("budgeting.view").allowed
  const workQueueFeature = useFeatureAccess("workQueue.view").allowed

  const financeAllowed = enabled && finance.canView
  const membersAllowed = enabled && members.canView
  const budgetAllowed = enabled && budgetingFeature && budgeting.canView
  const approvalsAllowed = enabled && workQueueFeature

  const scope = { workspaceId, branchId: activeBranchId }
  const financeQuery = useFinanceHomeQuery({ ...scope, dateFrom: period.from, dateTo: period.to }, { enabled: financeAllowed })
  const membersQuery = useMembersHomeQuery(scope, { enabled: membersAllowed })
  const budgetQuery = useActiveBudgetOverviewQuery(
    {
      workspaceId: workspaceId ?? undefined,
      context: BudgetScope.Branch,
      branchId: activeBranchId ?? undefined,
      dateFrom: period.from,
      dateTo: period.to,
    },
    { enabled: budgetAllowed && Boolean(period.from && period.to) },
  )
  const approvalsQuery = useWorkQueueBranchSummaryQuery(activeBranchId, { enabled: approvalsAllowed })

  const financeState = tileStateFor(financeAllowed, financeQuery)
  const membersState = tileStateFor(membersAllowed, membersQuery)
  const budgetState = tileStateFor(budgetAllowed, budgetQuery)
  // Shown even with nothing pending ("all clear"): on a configurable board a tile that vanishes with its data
  // would shuffle every tile after it. The reader can still hide it.
  const approvalsState = tileStateFor(approvalsAllowed, approvalsQuery)
  // Spending is money: withheld figures mean no tile here (the treasury tile already says why).
  const spendingState = tileStateFor(financeAllowed, financeQuery, financeQuery.data ? financeQuery.data.disclosed : true)

  const states = React.useMemo<Record<HomeTileId, HomeTileState>>(
    () =>
      enabled
        ? {
            [HomeTileId.Treasury]: financeState,
            [HomeTileId.Approvals]: approvalsState,
            [HomeTileId.Members]: membersState,
            [HomeTileId.RecentVisitors]: membersState,
            [HomeTileId.Birthdays]: membersState,
            [HomeTileId.NewMembers]: membersState,
            [HomeTileId.Budget]: budgetState,
            [HomeTileId.Spending]: spendingState,
          }
        : ABSENT_STATES,
    [enabled, financeState, approvalsState, membersState, budgetState, spendingState],
  )

  const firstName = user?.firstName?.trim() || null
  const dateLabel = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(churchNow)

  return React.useMemo(
    () => ({
      states,
      finance: financeQuery.data,
      members: membersQuery.data,
      budget: budgetQuery.data,
      approvals: approvalsQuery.data,
      can: { editMembers: members.canEdit, recordIncome: finance.canEdit, createBudget: budgeting.canEdit },
      href,
      firstName,
      dateLabel,
    }),
    [
      states,
      financeQuery.data,
      membersQuery.data,
      budgetQuery.data,
      approvalsQuery.data,
      members.canEdit,
      finance.canEdit,
      budgeting.canEdit,
      href,
      firstName,
      dateLabel,
    ],
  )
}
